/**
 * Fixed 20 Hz sim tick + display-rate interpolation (~60 Hz).
 *
 * Pattern: accumulator with max catch-up; render uses alpha between
 * previous and current sim snapshots. Pure orchestration — no Three/DOM.
 * Transform hitstop pauses sim ticks briefly for juice.
 */

import {
  TICK_DT,
  TICK_HZ,
  acceptContinue,
  createRun,
  declineContinue,
  setInput,
  snapshot,
  startRun,
  tick,
  type CreateRunOptions,
  type LevelDef,
  type SimEvent,
  type SimInput,
  type SimSnapshot,
  type WaveCombatState,
} from "@afr/sim";

export { TICK_DT, TICK_HZ };

/** Max sim catch-up per display frame (avoids spiral of death). */
export const MAX_FRAME_DT = 0.25;
/** Cap sim ticks processed in one display frame. */
export const MAX_TICKS_PER_FRAME = 5;
/** Default transform hitstop (matches render-three TRANSFORM_HITSTOP_SECONDS). */
export const DEFAULT_TRANSFORM_HITSTOP = 0.14;

export interface RunLoopOptions extends CreateRunOptions {
  readonly autoStart?: boolean;
  /** Hitstop duration after pedestal transform (seconds). 0 disables. */
  readonly transformHitstopSeconds?: number;
}

export interface RunLoopFrame {
  /** Interpolated snapshot for rendering. */
  readonly renderSnap: SimSnapshot;
  /** Latest authoritative sim snapshot (post-tick). */
  readonly simSnap: SimSnapshot;
  /** Interpolation alpha in [0, 1) between prev and current. */
  readonly alpha: number;
  /** Events from all sim ticks this frame. */
  readonly events: readonly SimEvent[];
  /** How many fixed ticks advanced this frame. */
  readonly ticksAdvanced: number;
  readonly frameDt: number;
  /** True while transform hitstop freezes sim. */
  readonly hitstopActive: boolean;
}

/**
 * Lerp continuous fields between two sim snapshots.
 * Discrete fields (n, phase, events, formation topology) come from `next`.
 */
export function interpolateSimSnapshots(
  prev: SimSnapshot,
  next: SimSnapshot,
  alpha: number,
): SimSnapshot {
  const t = Math.max(0, Math.min(1, alpha));
  const lerp = (a: number, b: number) => a + (b - a) * t;

  let formation = next.formation;
  if (prev.formation.length === next.formation.length && next.formation.length > 0) {
    formation = next.formation.map((slot, i) => {
      const p = prev.formation[i]!;
      return {
        ...slot,
        x: lerp(p.x, slot.x),
        z: lerp(p.z, slot.z),
        radius: lerp(p.radius, slot.radius),
        angle: lerp(p.angle, slot.angle),
      };
    });
  }

  let mecha = next.mecha;
  if (prev.mecha && next.mecha) {
    mecha = {
      ...next.mecha,
      hp: lerp(prev.mecha.hp, next.mecha.hp),
    };
  }

  return {
    ...next,
    pathZ: lerp(prev.pathZ, next.pathZ),
    lateralX: lerp(prev.lateralX, next.lateralX),
    formation,
    mecha,
    events: next.events,
  };
}

export type RunHandle = ReturnType<typeof createRun>;

/**
 * Owns a sim run and advances it on a fixed timestep while producing
 * interpolated snapshots for the render thread.
 */
export class RunLoop {
  readonly tickHz = TICK_HZ;
  readonly tickDt = TICK_DT;

  private run: RunHandle;
  private accumulator = 0;
  private prevSnap: SimSnapshot;
  private currSnap: SimSnapshot;
  private input: SimInput = { lateral: 0 };
  private started = false;
  private readonly triggeredGateIds = new Set<string>();
  private hitstopRemaining = 0;
  private readonly transformHitstopSeconds: number;
  private transformedThisRun = false;

  constructor(options: RunLoopOptions) {
    this.run = createRun(options);
    this.transformHitstopSeconds =
      options.transformHitstopSeconds ?? DEFAULT_TRANSFORM_HITSTOP;
    this.prevSnap = snapshot(this.run);
    this.currSnap = this.prevSnap;
    if (options.autoStart) {
      this.start();
    }
  }

  get level(): LevelDef {
    return this.run.config.level;
  }

  get isStarted(): boolean {
    return this.started;
  }

  get isHitstopActive(): boolean {
    return this.hitstopRemaining > 0;
  }

  get maxContinues(): number {
    return this.run.config.maxContinues;
  }

  /** Gate ids that have fired at least once this run (for marker hide). */
  getTriggeredGateIds(): readonly string[] {
    return [...this.triggeredGateIds];
  }

  start(): void {
    startRun(this.run);
    this.started = true;
    this.accumulator = 0;
    this.triggeredGateIds.clear();
    this.hitstopRemaining = 0;
    this.transformedThisRun = false;
    this.prevSnap = snapshot(this.run);
    this.currSnap = this.prevSnap;
  }

  setLateral(lateral: number): void {
    this.input = {
      lateral: Math.max(-1, Math.min(1, lateral)),
    };
  }

  getInput(): SimInput {
    return this.input;
  }

  /**
   * Accept continue offer (restore N or mecha HP). Returns false if not offered.
   */
  acceptContinue(): boolean {
    const ok = acceptContinue(this.run);
    if (ok) {
      this.prevSnap = snapshot(this.run);
      this.currSnap = this.prevSnap;
      this.accumulator = 0;
    }
    return ok;
  }

  /**
   * Decline continue → results_lose.
   */
  declineContinue(): void {
    declineContinue(this.run);
    this.prevSnap = snapshot(this.run);
    this.currSnap = this.prevSnap;
    this.accumulator = 0;
  }

  /**
   * Advance the loop by one display-frame delta (seconds).
   * Call from rAF (~60 Hz) or tests with fixed dt.
   * During transform hitstop, sim ticks freeze (juice).
   */
  step(frameDtSeconds: number): RunLoopFrame {
    const frameDt = Math.min(MAX_FRAME_DT, Math.max(0, frameDtSeconds));
    const frameEvents: SimEvent[] = [];
    let ticksAdvanced = 0;

    // Drain hitstop before sim catch-up
    if (this.hitstopRemaining > 0) {
      this.hitstopRemaining = Math.max(0, this.hitstopRemaining - frameDt);
    }

    if (this.started && this.hitstopRemaining <= 0) {
      this.accumulator += frameDt;
      while (
        this.accumulator >= TICK_DT &&
        ticksAdvanced < MAX_TICKS_PER_FRAME
      ) {
        setInput(this.run, this.input);
        this.prevSnap = this.currSnap;
        this.currSnap = tick(this.run, TICK_DT);
        for (const e of this.currSnap.events) {
          frameEvents.push(e);
          if (e.type === "gate_applied") {
            this.triggeredGateIds.add(e.gateId);
          }
          if (e.type === "transform" && !this.transformedThisRun) {
            this.transformedThisRun = true;
            this.hitstopRemaining = this.transformHitstopSeconds;
          }
        }
        this.accumulator -= TICK_DT;
        ticksAdvanced += 1;
        // Stop catching up if we just entered hitstop mid-frame
        if (this.hitstopRemaining > 0) {
          break;
        }
      }
      // Drop excess if we hit the tick cap to avoid permanent lag debt
      if (ticksAdvanced >= MAX_TICKS_PER_FRAME && this.accumulator > TICK_DT) {
        this.accumulator = this.accumulator % TICK_DT;
      }
    }

    const alpha =
      this.started && ticksAdvanced > 0
        ? Math.min(1, this.accumulator / TICK_DT)
        : this.started
          ? Math.min(1, this.accumulator / TICK_DT)
          : 0;

    const renderSnap = interpolateSimSnapshots(
      this.prevSnap,
      this.currSnap,
      alpha,
    );

    const withGates: SimSnapshot = {
      ...renderSnap,
      events: frameEvents,
    };

    return {
      renderSnap: withGates,
      simSnap: this.currSnap,
      alpha,
      events: frameEvents,
      ticksAdvanced,
      frameDt,
      hitstopActive: this.hitstopRemaining > 0,
    };
  }

  /** Current authoritative snapshot (no interpolation). */
  getSimSnapshot(): SimSnapshot {
    return this.currSnap;
  }

  /**
   * Apply environmental prop damage (explosive canisters) to the horde via the
   * same Model B aggregate wave HP that `resolveCombatTick` writes.
   *
   * Sim has no dedicated prop-damage API (frozen engine-v0.1); we mutate the
   * active / targeted WaveCombatState.hp surface that combat already owns, then
   * refresh the snapshot so death juice + win checks see the reduced HP.
   *
   * @returns true if any HP was applied to a living wave
   */
  applyPropDamage(damage: number, preferredWaveId?: string | null): boolean {
    if (!(damage > 0) || !this.started) return false;

    const run = this.run;
    const pickWave = (): WaveCombatState | null => {
      // Prefer explicit placement wave if still alive
      if (preferredWaveId) {
        for (const w of run.waves) {
          if (!w.cleared && w.def.id === preferredWaveId) return w;
        }
        if (
          run.climax &&
          !run.climax.cleared &&
          run.climax.def.id === preferredWaveId
        ) {
          return run.climax;
        }
      }
      // Active contact wave
      if (run.activeWave && !run.activeWave.cleared) return run.activeWave;
      // Nearest living wave by pathZ
      let best: WaveCombatState | null = null;
      let bestDist = Infinity;
      const candidates: WaveCombatState[] = [...run.waves];
      if (run.climax) candidates.push(run.climax);
      for (const w of candidates) {
        if (w.cleared) continue;
        const d = Math.abs(w.def.z - run.pathZ);
        if (d < bestDist) {
          bestDist = d;
          best = w;
        }
      }
      return best;
    };

    const wave = pickWave();
    if (!wave || wave.cleared || wave.hp <= 0) return false;

    const before = wave.hp;
    // Surface as active so snapshot.activeWaveHp drives horde death juice (w3)
    run.activeWave = wave;
    wave.hp = Math.max(0, wave.hp - damage);
    if (wave.hp <= 0) {
      wave.hp = 0;
      wave.cleared = true;
      wave.inContact = false;
      run.activeWave = null;
      // Climax clear → win (mirrors updateCombat climax path)
      if (run.climax === wave && run.inMecha) {
        run.phase = "results_win";
      }
    }

    // Refresh authoritative snap so render/horde see HP drop this frame
    this.currSnap = snapshot(run);
    return wave.hp < before || wave.cleared;
  }

  /** Replace level / rebuild run (idle until start). */
  reset(options: RunLoopOptions): void {
    this.run = createRun(options);
    this.started = false;
    this.accumulator = 0;
    this.triggeredGateIds.clear();
    this.hitstopRemaining = 0;
    this.transformedThisRun = false;
    this.prevSnap = snapshot(this.run);
    this.currSnap = this.prevSnap;
    this.input = { lateral: 0 };
    if (options.autoStart) this.start();
  }
}
